// src/components/admin/AdminKnowledgeBase.jsx
import React, { useState, useEffect, useCallback } from "react";
import { styled } from "@mui/material/styles";

import { kbFaqService }     from "../../services/KbFaqService";
import { kbArticleService } from "../../services/KbArticleService";

import {
  Box, Container, Typography, Paper, Button, Grid,
  TextField, IconButton, Chip, Alert, InputAdornment,
  Divider, Dialog, AppBar, Toolbar, Slide, Stack,
  FormControl, InputLabel, Select, MenuItem, FormHelperText,
  Accordion, AccordionSummary, AccordionDetails,
  Tabs, Tab, Snackbar, Tooltip, Fade, Skeleton,
  Card, CardContent, CircularProgress,
} from "@mui/material";

import AddIcon        from "@mui/icons-material/Add";
import EditIcon       from "@mui/icons-material/Edit";
import DeleteIcon     from "@mui/icons-material/Delete";
import SearchIcon     from "@mui/icons-material/Search";
import ClearIcon      from "@mui/icons-material/Clear";
import CloseIcon      from "@mui/icons-material/Close";
import QuizIcon       from "@mui/icons-material/Quiz";
import ArticleIcon    from "@mui/icons-material/Article";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// ─── Helpers ────────────────────────────────────────────────────────────────

const unwrap = (doc) => doc?.details || doc || {};


const flatten = (categories, itemsKey) => {
  const out = [];
  (categories || []).forEach((cat) => {
    const src = unwrap(cat);
    const items = Array.isArray(src[itemsKey]) ? src[itemsKey] : [];
    items.forEach((item) => {
      out.push({ ...item, categoryId: cat.id, category: src.name || "" });
    });
  });
  return out;
};

const parseTags = (raw) =>
  raw
    ? raw.split(",").map((t) => t.trim()).filter(Boolean)
    : [];

// ─── Slide Transition ───────────────────────────────────────────────────────
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// ─── Styled components (unchanged) ──────────────────────────────────────────
const FormRow = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: "1rem",
  marginBottom: "1rem",
  "&:last-child": { marginBottom: 0 },
  [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
}));

const FormGroup = styled(Box, {
  shouldForwardProp: (p) => p !== "hasError" && p !== "layoutType",
})(({ hasError, layoutType }) => ({
  gridColumn:
    layoutType === "full"    ? "1 / -1" :
    layoutType === "compact" ? "span 1" : "span 2",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: "0.375rem",
  "& .MuiOutlinedInput-root": {
    background: "#fff",
    "& fieldset":             { borderColor: hasError ? "#dc2626" : "#d1d5db" },
    "&:hover fieldset":       { borderColor: hasError ? "#dc2626" : "#3b82f6" },
    "&.Mui-focused fieldset": {
      borderColor: hasError ? "#dc2626" : "#3b82f6",
      boxShadow:   hasError
        ? "0 0 0 3px rgba(220,38,38,0.12)"
        : "0 0 0 3px rgba(59,130,246,0.12)",
    },
  },
}));

const FieldLabel = styled(Typography)(() => ({
  fontSize: "0.8125rem",
  fontWeight: 600,
  color: "#374151",
  textTransform: "uppercase",
  letterSpacing: "0.025em",
}));

const RequiredMark = styled("span")(() => ({
  color: "#ef4444",
  marginLeft: "0.125rem",
}));

const FieldHint = styled(Typography)(() => ({
  fontSize: "0.75rem",
  color: "#9ca3af",
  fontStyle: "italic",
}));

const SectionCard = styled(Card)(() => ({
  borderRadius: 8,
  border: "1px solid #e2e8f0",
}));

const SectionTitle = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
  fontSize: "0.9375rem",
  fontWeight: 700,
  color: "#1e293b",
  marginBottom: "1.25rem",
  paddingBottom: "0.75rem",
  borderBottom: "1px solid #e5e7eb",
}));

const cancelBtnSx = {
  px: 3, py: 1,
  backgroundColor: "#f3f4f6",
  color: "#374151",
  border: "1px solid #d1d5db",
  borderRadius: "6px",
  fontWeight: 500,
  textTransform: "none",
  "&:hover": { backgroundColor: "#e5e7eb" },
  "&:disabled": { opacity: 0.6 },
};

const submitBtnSx = {
  px: 3, py: 1,
  backgroundColor: "#2563eb",
  color: "#fff",
  borderRadius: "6px",
  fontWeight: 600,
  textTransform: "none",
  minWidth: "160px",
  display: "flex",
  alignItems: "center",
  gap: 1,
  "&:hover": { backgroundColor: "#1d4ed8" },
  "&:disabled": { backgroundColor: "#93c5fd" },
};

// ─── Skeleton (unchanged) ───────────────────────────────────────────────────
const KnowledgeBaseSkeleton = () => (
  <Container maxWidth="xl" sx={{ mt: 4, mb: 4, display: "flex", flexDirection: "column", gap: 3 }}>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <Box>
        <Skeleton variant="text" width={320} height={44} />
        <Skeleton variant="text" width={400} height={20} />
      </Box>
      <Skeleton variant="rounded" width={130} height={40} />
    </Box>
    <Box sx={{ borderRadius: 3, border: "1px solid #e0e0e0", background: "#fff", overflow: "hidden" }}>
      <Box sx={{ display: "flex", gap: 2, px: 2, py: 1, borderBottom: "1px solid #e0e0e0" }}>
        <Skeleton variant="rounded" width={120} height={36} />
        <Skeleton variant="rounded" width={120} height={36} />
      </Box>
      <Box sx={{ p: 2.5 }}>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Skeleton variant="rounded" width="66%" height={40} />
          <Skeleton variant="rounded" width="33%" height={40} />
        </Box>
      </Box>
      <Box sx={{ borderTop: "1px solid #e0e0e0" }}>
        {Array.from({ length: 5 }, (_, i) => (
          <Box key={i} sx={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            px: 3, py: 2, borderBottom: "1px solid #f5f5f5",
          }}>
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="60%" height={18} sx={{ mb: 0.75 }} />
              <Box sx={{ display: "flex", gap: 0.75 }}>
                <Skeleton variant="rounded" width={80} height={22} />
                <Skeleton variant="rounded" width={60} height={22} />
                <Skeleton variant="rounded" width={70} height={22} />
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <Skeleton variant="circular" width={32} height={32} />
              <Skeleton variant="circular" width={32} height={32} />
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  </Container>
);

// ─── Main Component ─────────────────────────────────────────────────────────
const AdminKnowledgeBase = () => {
  const [activeTab,        setActiveTab]        = useState("faqs");
  const [showModal,        setShowModal]        = useState(false);
  const [modalType,        setModalType]        = useState("add");
  const [currentItem,      setCurrentItem]      = useState(null);
  const [searchQuery,      setSearchQuery]      = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [loading,          setLoading]          = useState(true);
  const [saving,           setSaving]           = useState(false);
  const [alert,            setAlert]            = useState({ show: false, type: "success", message: "" });

  const [faqForm,     setFaqForm]     = useState({ category: "", question: "", answer: "", tags: "" });
  const [articleForm, setArticleForm] = useState({ category: "", title: "", content: "", tags: "" });
  const [errors,      setErrors]      = useState({});

  // Flattened, ready-to-render lists
  const [faqs,             setFaqs]             = useState([]);
  const [articles,         setArticles]         = useState([]);
  const [filteredFaqs,     setFilteredFaqs]     = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);

  // Category lists for dropdowns
  const [faqCategories,     setFaqCategories]     = useState([]);
  const [articleCategories, setArticleCategories] = useState([]);

  // ── On mount ─────────────────────────────────────────────────────────────
  useEffect(() => { loadData(); }, []);
  useEffect(() => { filterData(); }, [searchQuery, selectedCategory, faqs, articles, activeTab]);

  // ── Scroll lock (unchanged) ──────────────────────────────────────────────
  useEffect(() => {
    if (!showModal) return undefined;
    const html = document.documentElement;
    const previousOverflow = html.style.overflow;
    const previousPaddingRight = html.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    html.style.overflow = "hidden";
    if (scrollbarWidth > 0) html.style.paddingRight = `${scrollbarWidth}px`;
    return () => {
      html.style.overflow = previousOverflow;
      html.style.paddingRight = previousPaddingRight;
    };
  }, [showModal]);

  // ── loadData — source of truth is repo, via the two services ─────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [faqCats, artCats] = await Promise.all([
        kbFaqService.fetchCategories(),
        kbArticleService.fetchCategories(),
      ]);

      setFaqCategories(faqCats || []);
      setArticleCategories(artCats || []);
      setFaqs(flatten(faqCats, "questions"));
      setArticles(flatten(artCats, "articles"));
    } catch (error) {
      showAlert("error", "Failed to load data: " + (error?.message || error));
    } finally {
      setLoading(false);
    }
  }, []);

  // ── filterData (unchanged logic) ─────────────────────────────────────────
  const filterData = () => {
    if (activeTab === "faqs") {
      const filtered = faqs.filter((faq) => {
        const matchesSearch =
          !searchQuery ||
          (faq.question || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
          (faq.answer   || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
          (faq.tags || []).some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()));
        const matchesCategory = !selectedCategory || faq.categoryId === selectedCategory;
        return matchesSearch && matchesCategory;
      });
      setFilteredFaqs(filtered);
    } else {
      const filtered = articles.filter((article) => {
        const matchesSearch =
          !searchQuery ||
          (article.title   || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
          (article.content || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
          (article.tags || []).some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()));
        const matchesCategory = !selectedCategory || article.categoryId === selectedCategory;
        return matchesSearch && matchesCategory;
      });
      setFilteredArticles(filtered);
    }
  };

  // ── Validation (unchanged, still client-side UX; model re-validates) ─────
  const validateFAQ = (faq) => {
    const errs = {};
    if (!faq.category?.trim())                    errs.category = "Category is required";
    if (!faq.question?.trim())                    errs.question = "Question is required";
    if (!faq.answer?.trim())                      errs.answer   = "Answer is required";
    if (faq.question && faq.question.length < 10) errs.question = "Question must be at least 10 characters";
    if (faq.answer   && faq.answer.length   < 20) errs.answer   = "Answer must be at least 20 characters";
    return errs;
  };

  const validateArticle = (article) => {
    const errs = {};
    if (!article.category?.trim())                        errs.category = "Category is required";
    if (!article.title?.trim())                           errs.title    = "Title is required";
    if (!article.content?.trim())                         errs.content  = "Content is required";
    if (article.title   && article.title.length   < 5)   errs.title    = "Title must be at least 5 characters";
    if (article.content && article.content.length < 50)  errs.content  = "Content must be at least 50 characters";
    return errs;
  };

  // ── Modal helpers (unchanged) ────────────────────────────────────────────
  const openAddModal = () => {
    setModalType("add");
    setCurrentItem(null);
    if (activeTab === "faqs") {
      setFaqForm({ category: "", question: "", answer: "", tags: "" });
    } else {
      setArticleForm({ category: "", title: "", content: "", tags: "" });
    }
    setErrors({});
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setModalType("edit");
    setCurrentItem(item);
    if (activeTab === "faqs") {
      setFaqForm({
        category: item.categoryId,
        question: item.question,
        answer:   item.answer,
        tags:     (item.tags || []).join(", "),
      });
    } else {
      setArticleForm({
        category: item.categoryId,
        title:    item.title,
        content:  item.content,
        tags:     (item.tags || []).join(", "),
      });
    }
    setErrors({});
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setCurrentItem(null);
    setErrors({});
  };

  // ── handleSave — calls the new async services ────────────────────────────
  const handleSave = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setSaving(true);
    try {
      if (activeTab === "faqs") {
        const validationErrors = validateFAQ(faqForm);
        if (Object.keys(validationErrors).length > 0) {
          setErrors(validationErrors);
          return;
        }
        const faqData = {
          question: faqForm.question.trim(),
          answer:   faqForm.answer.trim(),
          tags:     parseTags(faqForm.tags),
        };

        if (modalType === "add") {
          await kbFaqService.addFaq(faqForm.category, faqData);
          showAlert("success", "FAQ added successfully");
        } else {
          await kbFaqService.updateFaq(currentItem.categoryId, currentItem.id, faqData);
          showAlert("success", "FAQ updated successfully");
        }
      } else {
        const validationErrors = validateArticle(articleForm);
        if (Object.keys(validationErrors).length > 0) {
          setErrors(validationErrors);
          return;
        }
        const articleData = {
          title:   articleForm.title.trim(),
          content: articleForm.content.trim(),
          tags:    parseTags(articleForm.tags),
        };

        if (modalType === "add") {
          await kbArticleService.addArticle(articleForm.category, articleData);
          showAlert("success", "Article added successfully");
        } else {
          await kbArticleService.updateArticle(currentItem.categoryId, currentItem.id, articleData);
          showAlert("success", "Article updated successfully");
        }
      }
      closeModal();
      await loadData();   // refresh from repo
    } catch (error) {
      showAlert("error", "Operation failed: " + (error?.message || error));
    } finally {
      setSaving(false);
    }
  };

  // ── handleDelete — calls the new async services ──────────────────────────
  const handleDelete = async (item) => {
    if (!window.confirm(
      `Are you sure you want to delete this ${activeTab === "faqs" ? "FAQ" : "article"}?`,
    )) return;
    try {
      if (activeTab === "faqs") {
        await kbFaqService.deleteFaq(item.categoryId, item.id);
        showAlert("success", "FAQ deleted successfully");
      } else {
        await kbArticleService.deleteArticle(item.categoryId, item.id);
        showAlert("success", "Article deleted successfully");
      }
      await loadData();
    } catch (error) {
      showAlert("error", "Delete failed: " + (error?.message || error));
    }
  };

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert({ show: false, type: "success", message: "" }), 5000);
  };

  // ── Derived helpers ──────────────────────────────────────────────────────
  const getCurrentCategories = () =>
    activeTab === "faqs" ? faqCategories : articleCategories;

  const getCurrentData = () =>
    activeTab === "faqs" ? filteredFaqs : filteredArticles;

  if (loading) return <KnowledgeBaseSkeleton />;

  const itemLabel  = activeTab === "faqs" ? "FAQ" : "Article";
  const modalTitle = `${modalType === "add" ? "Add" : "Edit"} ${itemLabel}`;

  // ═════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═════════════════════════════════════════════════════════════════════════
  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4, display: "flex", flexDirection: "column", gap: 3 }}>

      {/* HEADER */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h4" component="h1"
            sx={{ fontWeight: 700, color: "text.primary", letterSpacing: "-0.5px" }}>
            Knowledge Base Administration
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage FAQs and articles for your organisation's knowledge base
          </Typography>
        </Box>
        <Button variant="contained" color="primary" startIcon={<AddIcon />}
          onClick={openAddModal} disableElevation
          sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, px: 2.5 }}>
          Add {itemLabel}
        </Button>
      </Box>

      {/* SNACKBAR */}
      <Snackbar open={alert.show} autoHideDuration={5000}
        onClose={() => setAlert({ show: false, type: "success", message: "" })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert severity={alert.type === "error" ? "error" : "success"}
          onClose={() => setAlert({ show: false, type: "success", message: "" })}
          sx={{ fontWeight: 500 }}>
          {alert.message}
        </Alert>
      </Snackbar>

      {/* MAIN PANEL */}
      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Tabs value={activeTab}
          onChange={(_, v) => { setActiveTab(v); setSelectedCategory(""); setSearchQuery(""); }}
          sx={{
            px: 2, bgcolor: "background.default",
            "& .MuiTab-root": { textTransform: "none", fontWeight: 600, minHeight: 52 },
          }}>
          <Tab value="faqs"     icon={<QuizIcon />}    iconPosition="start" label={`FAQs (${faqs.length})`} />
          <Tab value="articles" icon={<ArticleIcon />} iconPosition="start" label={`Articles (${articles.length})`} />
        </Tabs>

        <Divider />

        {/* SEARCH + FILTER */}
        <Box sx={{ p: 2.5 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <TextField fullWidth size="small" variant="outlined"
                label={`Search ${activeTab}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "action.active" }} fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchQuery("")}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>All Categories</InputLabel>
                <Select value={selectedCategory} label="All Categories"
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  sx={{ borderRadius: 2 }}>
                  <MenuItem value="">All Categories</MenuItem>
                  {getCurrentCategories().map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {unwrap(cat).name || cat.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {searchQuery && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              Found <strong>{getCurrentData().length}</strong>{" "}
              {activeTab === "faqs" ? "FAQ" : "article"}
              {getCurrentData().length !== 1 ? "s" : ""} matching "<em>{searchQuery}</em>"
            </Typography>
          )}
        </Box>

        <Divider />

        {/* LIST */}
        <Box sx={{ p: 0 }}>
          {getCurrentData().length === 0 ? (
            <Box sx={{
              p: 8, textAlign: "center",
              display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5,
            }}>
              <ArticleIcon sx={{ fontSize: 56, color: "action.disabled" }} />
              <Typography variant="h6" color="text.secondary" fontWeight={600}>
                No {activeTab} found
              </Typography>
              <Typography variant="body2" color="text.disabled">
                {searchQuery || selectedCategory
                  ? "No items match your current filters."
                  : `Start by adding your first ${activeTab === "faqs" ? "FAQ" : "article"}.`}
              </Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={openAddModal}
                sx={{ mt: 1, borderRadius: 2, textTransform: "none", fontWeight: 600 }}
                disableElevation>
                Add {itemLabel}
              </Button>
            </Box>
          ) : (
            <Box>
              {getCurrentData().map((item, index) => (
                <Fade in key={item.id}>
                  <Accordion disableGutters elevation={0} square sx={{
                    borderBottom: index < getCurrentData().length - 1 ? "1px solid" : "none",
                    borderColor: "divider",
                    "&:before": { display: "none" },
                    "&.Mui-expanded": { margin: 0 },
                  }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{
                      px: 3, py: 1.5,
                      "&:hover": { bgcolor: "action.hover" },
                      "& .MuiAccordionSummary-content": {
                        mr: 1, alignItems: "center", justifyContent: "space-between",
                      },
                    }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={700} color="text.primary" sx={{ mb: 0.5 }}>
                          {activeTab === "faqs" ? item.question : item.title}
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          <Chip label={item.category} size="small" color="success"
                            sx={{ fontWeight: 600, fontSize: "0.7rem" }} />
                          {item.tags?.map((tag, tagIndex) => (
                            <Chip key={tagIndex} label={tag} size="small" color="default" variant="outlined"
                              sx={{ fontSize: "0.68rem" }} />
                          ))}
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0, ml: 1 }}
                        onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Edit" arrow>
                          <IconButton size="small"
                            onClick={(e) => { e.stopPropagation(); openEditModal(item); }}
                            sx={{ color: "primary.main", "&:hover": { bgcolor: "rgba(21,101,192,0.08)" } }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete" arrow>
                          <IconButton size="small"
                            onClick={(e) => { e.stopPropagation(); handleDelete(item); }}
                            sx={{ color: "error.main", "&:hover": { bgcolor: "rgba(211,47,47,0.08)" } }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </AccordionSummary>

                    <AccordionDetails sx={{ px: 3, pb: 2.5, pt: 0 }}>
                      <Divider sx={{ mb: 2 }} />
                      <Typography variant="body2" color="text.secondary"
                        sx={{ whiteSpace: "pre-line", lineHeight: 1.8, fontSize: "0.95rem" }}>
                        {activeTab === "faqs" ? item.answer : item.content}
                      </Typography>
                      <Box sx={{ mt: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
                        <Typography variant="caption" color="text.disabled">
                          Created: {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "—"}
                        </Typography>
                        {item.updatedAt && (
                          <Typography variant="caption" color="text.disabled">
                            Updated: {new Date(item.updatedAt).toLocaleDateString()}
                          </Typography>
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Fade>
              ))}
            </Box>
          )}
        </Box>
      </Paper>

      {/* ADD / EDIT DIALOG */}
      <Dialog
        fullScreen
        open={showModal}
        onClose={!saving ? closeModal : undefined}
        TransitionComponent={Transition}
        PaperProps={{ sx: { display: "flex", flexDirection: "column", overflow: "hidden" } }}
      >
        <AppBar sx={{ position: "relative", flexShrink: 0 }} elevation={1}>
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={closeModal} disabled={saving} aria-label="Close">
              <CloseIcon />
            </IconButton>
            <Box sx={{ ml: 2, flex: 1 }}>
              <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                {modalTitle}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                {activeTab === "faqs"
                  ? "Fill in the question, answer and optional tags"
                  : "Fill in the title, content and optional tags"}
              </Typography>
            </Box>
          </Toolbar>
        </AppBar>

        <Box sx={{
          display: "flex", flexDirection: "column",
          bgcolor: "#f1f5f9", flex: 1, minHeight: 0, overflow: "hidden",
        }}>
          <Box component="form" id="kb-form" onSubmit={handleSave} noValidate
            sx={{ flex: 1, overflowY: "auto", py: 4 }}>
            <Container maxWidth="lg">
              <Stack spacing={3}>

                {activeTab === "faqs" && (
                  <>
                    <SectionCard variant="outlined">
                      <CardContent sx={{ p: 3 }}>
                        <SectionTitle>
                          <QuizIcon color="primary" fontSize="small" />
                          <Typography sx={{ fontSize: "0.9375rem", fontWeight: 700, color: "#1e293b" }}>
                            FAQ Classification
                          </Typography>
                        </SectionTitle>

                        <FormRow>
                          <FormGroup layoutType="normal" hasError={Boolean(errors.category)}>
                            <FieldLabel>Category <RequiredMark>*</RequiredMark></FieldLabel>
                            <FormControl fullWidth size="small" error={Boolean(errors.category)}>
                              <Select value={faqForm.category}
                                onChange={(e) => setFaqForm({ ...faqForm, category: e.target.value })}
                                displayEmpty>
                                <MenuItem value="">Select Category</MenuItem>
                                {faqCategories.map((cat) => (
                                  <MenuItem key={cat.id} value={cat.id}>
                                    {unwrap(cat).name || cat.id}
                                  </MenuItem>
                                ))}
                              </Select>
                              {errors.category && <FormHelperText>{errors.category}</FormHelperText>}
                            </FormControl>
                          </FormGroup>

                          <FormGroup layoutType="normal">
                            <FieldLabel>Tags</FieldLabel>
                            <TextField fullWidth size="small"
                              value={faqForm.tags}
                              onChange={(e) => setFaqForm({ ...faqForm, tags: e.target.value })}
                              placeholder="Enter tags separated by commas" />
                            <FieldHint>Separate multiple tags with commas</FieldHint>
                          </FormGroup>
                        </FormRow>
                      </CardContent>
                    </SectionCard>

                    <SectionCard variant="outlined">
                      <CardContent sx={{ p: 3 }}>
                        <SectionTitle>
                          <QuizIcon color="primary" fontSize="small" />
                          <Typography sx={{ fontSize: "0.9375rem", fontWeight: 700, color: "#1e293b" }}>
                            Question &amp; Answer
                          </Typography>
                        </SectionTitle>

                        <FormRow>
                          <FormGroup layoutType="full" hasError={Boolean(errors.question)}>
                            <FieldLabel>Question <RequiredMark>*</RequiredMark></FieldLabel>
                            <TextField fullWidth multiline rows={2} size="small"
                              value={faqForm.question}
                              onChange={(e) => setFaqForm({ ...faqForm, question: e.target.value })}
                              error={Boolean(errors.question)}
                              placeholder="Enter the question" />
                            <FieldHint sx={{ color: errors.question ? "#dc2626" : "#9ca3af" }}>
                              {errors.question || "Minimum 10 characters"}
                            </FieldHint>
                          </FormGroup>
                        </FormRow>

                        <FormRow>
                          <FormGroup layoutType="full" hasError={Boolean(errors.answer)}>
                            <FieldLabel>Answer <RequiredMark>*</RequiredMark></FieldLabel>
                            <TextField fullWidth multiline rows={8} size="small"
                              value={faqForm.answer}
                              onChange={(e) => setFaqForm({ ...faqForm, answer: e.target.value })}
                              error={Boolean(errors.answer)}
                              placeholder="Enter the detailed answer" />
                            <FieldHint sx={{ color: errors.answer ? "#dc2626" : "#9ca3af" }}>
                              {errors.answer || "Minimum 20 characters"}
                            </FieldHint>
                          </FormGroup>
                        </FormRow>
                      </CardContent>
                    </SectionCard>
                  </>
                )}

                {activeTab === "articles" && (
                  <>
                    <SectionCard variant="outlined">
                      <CardContent sx={{ p: 3 }}>
                        <SectionTitle>
                          <ArticleIcon color="primary" fontSize="small" />
                          <Typography sx={{ fontSize: "0.9375rem", fontWeight: 700, color: "#1e293b" }}>
                            Article Classification
                          </Typography>
                        </SectionTitle>

                        <FormRow>
                          <FormGroup layoutType="normal" hasError={Boolean(errors.category)}>
                            <FieldLabel>Category <RequiredMark>*</RequiredMark></FieldLabel>
                            <FormControl fullWidth size="small" error={Boolean(errors.category)}>
                              <Select value={articleForm.category}
                                onChange={(e) => setArticleForm({ ...articleForm, category: e.target.value })}
                                displayEmpty>
                                <MenuItem value="">Select Category</MenuItem>
                                {articleCategories.map((cat) => (
                                  <MenuItem key={cat.id} value={cat.id}>
                                    {unwrap(cat).name || cat.id}
                                  </MenuItem>
                                ))}
                              </Select>
                              {errors.category && <FormHelperText>{errors.category}</FormHelperText>}
                            </FormControl>
                          </FormGroup>

                          <FormGroup layoutType="normal">
                            <FieldLabel>Tags</FieldLabel>
                            <TextField fullWidth size="small"
                              value={articleForm.tags}
                              onChange={(e) => setArticleForm({ ...articleForm, tags: e.target.value })}
                              placeholder="Enter tags separated by commas" />
                            <FieldHint>Separate multiple tags with commas</FieldHint>
                          </FormGroup>
                        </FormRow>
                      </CardContent>
                    </SectionCard>

                    <SectionCard variant="outlined">
                      <CardContent sx={{ p: 3 }}>
                        <SectionTitle>
                          <ArticleIcon color="primary" fontSize="small" />
                          <Typography sx={{ fontSize: "0.9375rem", fontWeight: 700, color: "#1e293b" }}>
                            Article Content
                          </Typography>
                        </SectionTitle>

                        <FormRow>
                          <FormGroup layoutType="full" hasError={Boolean(errors.title)}>
                            <FieldLabel>Title <RequiredMark>*</RequiredMark></FieldLabel>
                            <TextField fullWidth size="small"
                              value={articleForm.title}
                              onChange={(e) => setArticleForm({ ...articleForm, title: e.target.value })}
                              error={Boolean(errors.title)}
                              placeholder="Enter article title" />
                            <FieldHint sx={{ color: errors.title ? "#dc2626" : "#9ca3af" }}>
                              {errors.title || "Minimum 5 characters"}
                            </FieldHint>
                          </FormGroup>
                        </FormRow>

                        <FormRow>
                          <FormGroup layoutType="full" hasError={Boolean(errors.content)}>
                            <FieldLabel>Content <RequiredMark>*</RequiredMark></FieldLabel>
                            <TextField fullWidth multiline rows={14} size="small"
                              value={articleForm.content}
                              onChange={(e) => setArticleForm({ ...articleForm, content: e.target.value })}
                              error={Boolean(errors.content)}
                              placeholder="Enter the article content" />
                            <FieldHint sx={{ color: errors.content ? "#dc2626" : "#9ca3af" }}>
                              {errors.content || "Minimum 50 characters"}
                            </FieldHint>
                          </FormGroup>
                        </FormRow>
                      </CardContent>
                    </SectionCard>
                  </>
                )}

              </Stack>
            </Container>
          </Box>

          <Box sx={{
            borderTop: "1px solid #e2e8f0",
            backgroundColor: "#fff",
            py: 2, px: 3,
            boxShadow: "0 -2px 8px rgba(0,0,0,0.04)",
            flexShrink: 0,
          }}>
            <Container maxWidth={false} disableGutters>
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
                <Button type="button" onClick={closeModal} disabled={saving} sx={cancelBtnSx}>
                  Cancel
                </Button>
                <Button type="submit" form="kb-form" disabled={saving} sx={submitBtnSx}>
                  {saving && <CircularProgress size={16} color="inherit" />}
                  {saving ? "Processing…" : "Submit"}
                </Button>
              </Box>
            </Container>
          </Box>
        </Box>
      </Dialog>

    </Container>
  );
};

export default AdminKnowledgeBase;